import { ref } from "vue";
import { loginRequest, msalInstance } from "../config/authConfig";
import { AccountInfo, InteractionRequiredAuthError } from "@azure/msal-browser";

export function useAuth() {
  const isAuthenticated = ref(false);
  const user = ref<AccountInfo | null>(null);
  const idTokenExpiry = ref(new Date().getTime());

  const initialize = async () => {
    try {
      await msalInstance.initialize();
    } catch (error) {
      console.log("Initialization error", error);
    }
  };

  const login = async () => {
    try {
      if (!msalInstance) {
        throw new Error("MSAL not initialized.");
      }
      await msalInstance.loginRedirect(loginRequest);
      isAuthenticated.value = true;
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const logout = () => {
    if (!msalInstance) {
      throw new Error("MSAL not initialized.");
    }
    msalInstance.logoutRedirect();
    isAuthenticated.value = false;
    user.value = null;
  };

  const handleRedirect = async () => {
    try {
      await msalInstance.handleRedirectPromise();
      isAuthenticated.value = msalInstance.getAllAccounts().length > 0;
      user.value = msalInstance.getAllAccounts()[0];
    } catch (error) {
      console.error("Redirect error:", error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      await initialize();
      await handleRedirect();
      isAuthenticated.value = msalInstance.getAllAccounts().length > 0;
      user.value = msalInstance.getAllAccounts()[0];
    } catch (error) {
      console.error("Redirect error:", error);
      return null;
    }
  };

  const refreshIdToken = async (forceRefresh: boolean = false) => {
    try {
      const account = msalInstance.getAllAccounts()[0];
      if (!account) {
        throw new Error("No account found");
      }
      let response = await msalInstance.acquireTokenSilent({
        account,
        scopes: loginRequest.scopes,
        forceRefresh: forceRefresh,
      });

      const now = new Date();
      const expiresOn = response.expiresOn;

      if (expiresOn && expiresOn.getTime() - now.getTime() <= 20 * 60 * 1000) {
        console.log("Token is about to expire, forcing refresh...");
        response = await msalInstance.acquireTokenSilent({
          account,
          scopes: loginRequest.scopes,
          forceRefresh: true,
        });
      }

      idTokenExpiry.value = expiresOn?.getTime() ?? now.getTime();
      return response.idToken;
    } catch (error) {
      console.error("Error refreshing token silently:", error);

      if (error instanceof InteractionRequiredAuthError) {
        await login();
      }
      return null;
    }
  };

  const forceRefreshIdToken = async () => {
    await refreshIdToken(true);
  };

  const getAuthHeaders = async () => {
    await fetchCurrentUser();
    const idToken = await refreshIdToken();

    return idToken
      ? {
          Authorization: `Bearer ${idToken}`,
        }
      : {};
  };

  const getAuthHeader = async () => {
    await fetchCurrentUser();
    return await refreshIdToken();
  };

  const authHeaderExpired = (): boolean => {
    return idTokenExpiry.value - new Date().getTime() <= 20 * 60 * 1000;
  };

  const getManagerEmail = async () => {
    await fetchCurrentUser();
    return user.value?.username ?? "";
  };

  return {
    initialize,
    login,
    logout,
    handleRedirect,
    isAuthenticated,
    user,
    fetchCurrentUser,
    getAuthHeaders,
    getAuthHeader,
    refreshIdToken,
    forceRefreshIdToken,
    authHeaderExpired,
    getManagerEmail,
  };
}
